/**
 * A tab or installed app that stays open across a deploy keeps running the
 * old bundle. Nothing reloads it: the socket reconnects and the board keeps
 * the old code. Compare our entry script with the one in the live
 * index.html; the router reloads on the next safe navigation.
 */
const ENTRY_RE = /<script[^>]+src="([^"]*\/assets\/index-[^"]+\.js)"/
const MIN_GAP_MS = 60_000
const POLL_MS = 5 * 60_000

let stale = false
let lastCheck = 0
let inFlight: Promise<void> | null = null

function ownEntry(): string | null {
  const el = document.querySelector<HTMLScriptElement>(
    'script[type="module"][src*="/assets/index-"]',
  )
  if (!el?.src) return null
  return new URL(el.src, location.href).pathname
}

async function check(): Promise<void> {
  const mine = ownEntry()
  if (!mine) return
  const res = await fetch(`/?build=${Date.now()}`, {
    cache: 'no-store',
    headers: { Accept: 'text/html' },
  })
  if (!res.ok) return
  const match = ENTRY_RE.exec(await res.text())
  if (!match) return
  const live = new URL(match[1], location.href).pathname
  if (live !== mine) stale = true
}

export function checkForNewBuild(): void {
  if (stale || inFlight) return
  const now = Date.now()
  if (now - lastCheck < MIN_GAP_MS) return
  lastCheck = now
  inFlight = check()
    .catch(() => {})
    .finally(() => {
      inFlight = null
    })
}

export function buildIsStale(): boolean {
  return stale
}

export function watchForNewBuild(): void {
  if (!import.meta.env.PROD) return
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') checkForNewBuild()
  })
  window.setInterval(() => {
    if (document.visibilityState === 'visible') checkForNewBuild()
  }, POLL_MS)
}
